'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as C from '@radix-ui/react-context-menu';
import { Children, cloneElement, forwardRef } from 'react';
import { CheckLine } from '@zealy/icons';
import { withBaseIcon } from '@zealy/icons/dist/src/Icons.utils';
import { cn } from '../../utils/cn';
import { Checkbox } from '../Checkbox';
import { z } from '../Primitive';
import { ContextMenuGroupProvider, ContextMenuProvider, useContextMenu, useContextMenuGroup, } from './ContextMenu.context';
import { contextMenuStyles as styles } from './ContextMenu.styles';
const Root = ({ children, size = 'md', ...props }) => {
    return (_jsx(ContextMenuProvider, { value: { size }, children: _jsx(C.Root, { ...props, children: children }) }));
};
const Trigger = forwardRef(({ className, children, ...props }, ref) => {
    return (_jsx(C.Trigger, { ...props, ref: ref, className: className, children: children }));
});
/**
 * ContextMenu Icon component
 */
export const Icon = forwardRef(({ children, className, size: sizeHTML, ...props }, ref) => {
    const { size } = useContextMenu();
    return withBaseIcon(cloneElement(Children.only(children), {
        ref: ref,
        className: cn(styles.icon({
            size,
        }), className),
    }), {
        ...props,
        size: sizeHTML,
    });
});
/**
 * ContextMenu Portal component
 */
const Portal = ({ children, ...props }) => {
    return _jsx(C.Portal, { ...props, children: children });
};
/**
 * ContextMenu Content component
 */
const Content = forwardRef(({ className, children, ...props }, ref) => {
    const { size } = useContextMenu();
    return (_jsx(C.Content, { "data-scope": "contextmenu", "data-part": "content", className: cn(styles.content({ size }), className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu Item component
 */
const Item = forwardRef(({ className, children, size: sizeProp, type: typeProp, leftIcon, rightIcon, ...props }, ref) => {
    const { size: sizeCtx } = useContextMenu();
    const { type: typeGroupCtx } = useContextMenuGroup();
    const size = sizeProp ?? sizeCtx;
    const type = typeProp ?? typeGroupCtx;
    return (_jsxs(C.Item, { "data-scope": "contextmenu", "data-part": "item", className: cn(styles.item({ size, type }), className), ...props, ref: ref, children: [leftIcon && _jsx(Anatomy.Icon, { children: leftIcon }), children, rightIcon && _jsx(Anatomy.Icon, { children: rightIcon })] }));
});
export const Shortcut = forwardRef(({ className, children, ...props }, ref) => {
    const { size } = useContextMenu();
    return (_jsx("span", { "data-scope": "contextmenu", "data-part": "shortcut", className: cn(styles.kbd({ size }), className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu Group component
 */
const ItemGroup = forwardRef(({ className, children, type = 'neutral', ...props }, ref) => {
    return (_jsx(ContextMenuGroupProvider, { value: { type }, children: _jsx(C.Group, { "data-scope": "contextmenu", "data-part": "itemgroup", className: cn(styles.group(), className), ...props, ref: ref, children: children }) }));
});
/**
 * ContextMenu Label component
 */
const GroupLabel = forwardRef(({ className, children, ...props }, ref) => {
    const { size } = useContextMenu();
    return (_jsx(C.Label, { "data-scope": "contextmenu", "data-part": "grouplabel", className: cn(styles.grouplabel({ size }), className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu CheckboxItem component
 */
const CheckboxItem = forwardRef(({ className, children, onSelect, closeOnSelect = false, size: sizeProp, type: typeProp, checked, onCheckedChange, leftIcon, rightIcon, ...props }, ref) => {
    const { size: sizeCtx } = useContextMenu();
    const { type: typeGroupCtx } = useContextMenuGroup();
    const size = sizeProp ?? sizeCtx;
    const type = typeProp ?? typeGroupCtx;
    function handleOnSelect(event) {
        if (!closeOnSelect) {
            event.preventDefault();
        }
        onSelect?.(event);
    }
    return (_jsx(C.CheckboxItem, { "data-scope": "contextmenu", "data-part": "item", className: cn(styles.item({
            size,
            type,
        }), '!pl-0', className), onSelect: handleOnSelect, checked: checked, onCheckedChange: onCheckedChange, ...props, ref: ref, children: _jsxs(Checkbox, { checked: checked, onCheckedChange: onCheckedChange, indicatorContainerClass: "group-hover:bg-transparent group-active:bg-transparent", children: [leftIcon && _jsx(Anatomy.Icon, { children: leftIcon }), children, rightIcon && _jsx(Anatomy.Icon, { children: rightIcon })] }) }));
});
/**
 * ContextMenu RadioGroup component
 */
const RadioGroup = forwardRef(({ className, children, ...props }, ref) => {
    return (_jsx(C.RadioGroup, { "data-scope": "contextmenu", "data-part": "radiogroup", className: cn(className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu RadioItem component
 */
const RadioItem = forwardRef(({ className, children, onSelect, closeOnSelect = false, size: sizeProp, type: typeProp, ...props }, ref) => {
    const { size: sizeCtx } = useContextMenu();
    const { type: typeGroupCtx } = useContextMenuGroup();
    const size = sizeProp ?? sizeCtx;
    const type = typeProp ?? typeGroupCtx;
    function handleOnSelect(event) {
        if (!closeOnSelect) {
            event.preventDefault();
        }
        onSelect?.(event);
    }
    return (_jsxs(C.RadioItem, { "data-scope": "contextmenu", "data-part": "radioitem", className: cn(styles.item({
            size,
            type,
        }), className), onSelect: handleOnSelect, ...props, ref: ref, children: [_jsx(z.div, { className: cn(styles.icon({ size })), children: _jsx(ItemIndicator, { children: _jsx(CheckLine, { className: cn(styles.icon({ size })) }) }) }), children] }));
});
/**
 * ContextMenu ItemIndicator component
 */
const ItemIndicator = forwardRef(({ className, children, ...props }, ref) => {
    return (_jsx(C.ItemIndicator, { "data-scope": "contextmenu", "data-part": "itemindicator", className: cn(className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu Separator component
 */
const Separator = forwardRef(({ className, children, ...props }, ref) => {
    return (_jsx(C.Separator, { "data-scope": "contextmenu", "data-part": "separator", className: cn(styles.separator(), className), ...props, ref: ref, children: children }));
});
/**
 * ContextMenu Sub component
 */
const SubMenu = ({ children, ...props }) => {
    return _jsx(C.Sub, { ...props, children: children });
};
/**
 * ContextMenu SubTrigger component
 */
const SubTrigger = forwardRef(({ className, children, ...props }, ref) => {
    const { size } = useContextMenu();
    return (_jsx(C.SubTrigger, { "data-scope": "contextmenu", "data-part": "subtrigger", className: className, ...props, ref: ref, children: children }));
});
/**
 * ContextMenu SubContent component
 */
const SubContent = forwardRef(({ className, children, ...props }, ref) => {
    const { size } = useContextMenu();
    return (_jsx(C.SubContent, { "data-scope": "contextmenu", "data-part": "subcontent", className: cn(styles.content({ size }), className), ...props, ref: ref, children: children }));
});
export const Anatomy = {
    Root,
    Trigger,
    Icon,
    Portal,
    Content,
    Item,
    ItemGroup,
    GroupLabel,
    CheckboxItem,
    RadioGroup,
    RadioItem,
    ItemIndicator,
    Shortcut,
    Separator,
    SubMenu,
    SubTrigger,
    SubContent,
};
